import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Search,
  Plus,
  Trash2,
  Save,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  ArrowUpDown,
  CheckCircle,
  AlertTriangle,
  Loader2,
  Download,
  Upload,
  AlertOctagon,
  Filter,
  XCircle,
} from 'lucide-react';
import { docProcessingApi, DomainTerm, DomainTermUpsertRequest } from '../../services/docProcessingApi';

const PAGE_SIZE = 25;

type SortField = 'term' | 'canonical_form' | 'category' | 'source' | 'confidence' | 'verified' | 'is_abbreviation' | 'full_form' | 'usage_count';
type SortDir = 'asc' | 'desc';

interface ColumnFilters {
  term: string;
  canonical_form: string;
  category: string;
  is_abbreviation: string; // 'all' | 'yes' | 'no'
  full_form: string;
  source: string;
  confidence: string;
  verified: string; // 'all' | 'yes' | 'no'
  usage_count: string;
}

const defaultFilters: ColumnFilters = {
  term: '',
  canonical_form: '',
  category: '',
  is_abbreviation: 'all',
  full_form: '',
  source: '',
  confidence: '',
  verified: 'all',
  usage_count: '',
};

export default function DomainTermsEditor() {
  const [terms, setTerms] = useState<DomainTerm[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [columnFilters, setColumnFilters] = useState<ColumnFilters>(defaultFilters);
  const [showColumnFilters, setShowColumnFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [sortField, setSortField] = useState<SortField>('term');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const [editingTerm, setEditingTerm] = useState<DomainTerm | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bulkAction, setBulkAction] = useState<string | null>(null);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [bulkDeleteSource, setBulkDeleteSource] = useState('');
  const [bulkDeleteCategory, setBulkDeleteCategory] = useState('');

  // Form fields
  const [formTerm, setFormTerm] = useState('');
  const [formCanonical, setFormCanonical] = useState('');
  const [formCategory, setFormCategory] = useState('');
  const [formIsAbbr, setFormIsAbbr] = useState(false);
  const [formFullForm, setFormFullForm] = useState('');
  const [formSource, setFormSource] = useState('');
  const [formConfidence, setFormConfidence] = useState('1');
  const [formVerified, setFormVerified] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await docProcessingApi.listDomainTerms();
      setTerms(result.entries);
    } catch (err: any) {
      setError(err.message || 'Failed to load domain terms');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Unique categories and sources for filter dropdowns
  const categories = useMemo(() => {
    const cats = new Set(terms.map((t) => t.category || '').filter(Boolean));
    return Array.from(cats).sort();
  }, [terms]);

  const sources = useMemo(() => {
    const srcs = new Set(terms.map((t) => t.source || '').filter(Boolean));
    return Array.from(srcs).sort();
  }, [terms]);

  const hasActiveColumnFilters = useMemo(() => {
    return Object.entries(columnFilters).some(([key, val]) => {
      if (key === 'is_abbreviation' || key === 'verified') return val !== 'all';
      return val !== '';
    });
  }, [columnFilters]);

  // Filter & sort
  const filtered = useMemo(() => {
    let list = terms;

    // Global search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (t) =>
          t.term.toLowerCase().includes(q) ||
          t.canonical_form.toLowerCase().includes(q) ||
          (t.full_form || '').toLowerCase().includes(q),
      );
    }

    // Column filters
    if (showColumnFilters || hasActiveColumnFilters) {
      if (columnFilters.term) {
        const q = columnFilters.term.toLowerCase();
        list = list.filter((t) => t.term.toLowerCase().includes(q));
      }
      if (columnFilters.canonical_form) {
        const q = columnFilters.canonical_form.toLowerCase();
        list = list.filter((t) => t.canonical_form.toLowerCase().includes(q));
      }
      if (columnFilters.category) {
        list = list.filter((t) => (t.category || '') === columnFilters.category);
      }
      if (columnFilters.is_abbreviation !== 'all') {
        const isAbbr = columnFilters.is_abbreviation === 'yes';
        list = list.filter((t) => t.is_abbreviation === isAbbr);
      }
      if (columnFilters.full_form) {
        const q = columnFilters.full_form.toLowerCase();
        list = list.filter((t) => (t.full_form || '').toLowerCase().includes(q));
      }
      if (columnFilters.source) {
        list = list.filter((t) => (t.source || '') === columnFilters.source);
      }
      if (columnFilters.confidence) {
        const threshold = parseFloat(columnFilters.confidence);
        if (!isNaN(threshold)) {
          list = list.filter((t) => (t.confidence ?? 0) >= threshold);
        }
      }
      if (columnFilters.verified !== 'all') {
        const isVerified = columnFilters.verified === 'yes';
        list = list.filter((t) => (t.verified ?? false) === isVerified);
      }
      if (columnFilters.usage_count) {
        const threshold = parseInt(columnFilters.usage_count, 10);
        if (!isNaN(threshold)) {
          list = list.filter((t) => (t.usage_count ?? 0) >= threshold);
        }
      }
    }

    list = [...list].sort((a, b) => {
      let av: any = (a as any)[sortField] ?? '';
      let bv: any = (b as any)[sortField] ?? '';
      if (typeof av === 'string') av = av.toLowerCase();
      if (typeof bv === 'string') bv = bv.toLowerCase();
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [terms, searchQuery, columnFilters, showColumnFilters, hasActiveColumnFilters, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);

  const toggleSort = (field: SortField) => {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('asc'); }
  };

  const updateColumnFilter = (field: keyof ColumnFilters, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [field]: value }));
    setCurrentPage(0);
  };

  const clearAllFilters = () => {
    setColumnFilters(defaultFilters);
    setSearchQuery('');
    setCurrentPage(0);
  };

  const openEditor = (term: DomainTerm | null) => {
    if (term) {
      setEditingTerm(term);
      setIsNew(false);
      setFormTerm(term.term);
      setFormCanonical(term.canonical_form);
      setFormCategory(term.category || '');
      setFormIsAbbr(term.is_abbreviation);
      setFormFullForm(term.full_form || '');
      setFormSource(term.source || '');
      setFormConfidence(String(term.confidence ?? 1));
      setFormVerified(term.verified ?? false);
    } else {
      setEditingTerm({} as DomainTerm);
      setIsNew(true);
      setFormTerm('');
      setFormCanonical('');
      setFormCategory('');
      setFormIsAbbr(false);
      setFormFullForm('');
      setFormSource('manual');
      setFormConfidence('1');
      setFormVerified(false);
    }
  };

  const handleSave = async () => {
    if (!formTerm.trim()) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const data: DomainTermUpsertRequest = {
        ...(editingTerm && !isNew ? { id: editingTerm.id } : {}),
        term: formTerm.trim(),
        canonical_form: formCanonical.trim() || formTerm.trim(),
        category: formCategory || null,
        is_abbreviation: formIsAbbr,
        full_form: formFullForm || null,
        source: formSource || 'manual',
        confidence: parseFloat(formConfidence) || 1,
        verified: formVerified,
      };
      const result = await docProcessingApi.upsertDomainTerm(data);
      const saved = result.entry;

      setTerms((prev) => {
        const idx = prev.findIndex((t) => t.id === saved.id);
        if (idx >= 0) {
          const updated = [...prev];
          updated[idx] = saved;
          return updated;
        }
        return [saved, ...prev];
      });

      setSuccess(isNew ? 'Term created' : 'Term updated');
      setEditingTerm(null);
    } catch (err: any) {
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingTerm || isNew) return;
    if (!window.confirm(`Delete term "${editingTerm.term}"? This will also delete all its synonym mappings.`)) return;
    const deletedId = editingTerm.id;
    setSaving(true);
    setSuccess(null);
    try {
      await docProcessingApi.deleteDomainTerm(deletedId);
      setTerms((prev) => prev.filter((t) => t.id !== deletedId));
      setSuccess(`Term "${editingTerm.term}" deleted`);
      setEditingTerm(null);
    } catch (err: any) {
      setError(err.message || 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  // ── Export / Wipe / Import ──────────────────────────────────

  const handleExport = async () => {
    setError(null);
    setSuccess(null);
    setBulkAction('Exporting terms and synonyms…');
    try {
      const data = await docProcessingApi.exportDomainTerms();
      const blob = new Blob([JSON.stringify(data.entries, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `domain-terms-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setSuccess(`Exported ${data.total_terms} terms and ${data.total_synonyms} synonyms`);
    } catch (err: any) {
      setError(err.message || 'Export failed');
    } finally {
      setBulkAction(null);
    }
  };

  const handleWipe = async () => {
    if (!window.confirm('This will DELETE ALL domain terms, synonym mappings, and chunk expansions. This cannot be undone.\n\nAre you sure?')) return;
    if (!window.confirm('FINAL CONFIRMATION: Wipe all domain terms and synonyms?')) return;
    setError(null);
    setSuccess(null);
    setBulkAction('Wiping all domain terms and synonyms…');
    try {
      const result = await docProcessingApi.wipeDomainTerms();
      setSuccess(`Wiped ${result.terms_deleted} terms and ${result.chunk_expansions_deleted} chunk expansions`);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Wipe failed');
    } finally {
      setBulkAction(null);
    }
  };

  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      setError(null);
      setSuccess(null);
      setBulkAction(`Importing from ${file.name}…`);
      try {
        const text = await file.text();
        const entries = JSON.parse(text);
        const list = Array.isArray(entries) ? entries : entries.entries;
        if (!Array.isArray(list)) {
          throw new Error('JSON must be an array of term objects, or { "entries": [...] }');
        }
        setBulkAction(`Importing ${list.length} terms from ${file.name}…`);
        const result = await docProcessingApi.importDomainTerms(list);
        let msg = `Imported ${result.terms_created} terms, ${result.synonyms_created} synonyms`;
        if (result.errors.length > 0) {
          msg += ` (${result.errors.length} errors)`;
        }
        setSuccess(msg);
        await loadData();
      } catch (err: any) {
        setError(err.message || 'Import failed');
      } finally {
        setBulkAction(null);
      }
    };
    input.click();
  };

  // ── Bulk Delete by Filter ──────────────────────────────────

  const handleBulkDelete = async () => {
    if (!bulkDeleteSource && !bulkDeleteCategory) return;
    const parts: string[] = [];
    if (bulkDeleteSource) parts.push(`source="${bulkDeleteSource}"`);
    if (bulkDeleteCategory) parts.push(`category="${bulkDeleteCategory}"`);
    const filterDesc = parts.join(' and ');
    if (!window.confirm(`Delete all domain terms where ${filterDesc}?\n\nThis cannot be undone.`)) return;
    setError(null);
    setSuccess(null);
    setShowBulkDeleteDialog(false);
    setBulkAction(`Deleting terms where ${filterDesc}…`);
    try {
      const result = await docProcessingApi.bulkDeleteDomainTerms(
        bulkDeleteSource || undefined,
        bulkDeleteCategory || undefined,
      );
      setSuccess(`Deleted ${result.terms_deleted} terms (filter: ${filterDesc})`);
      setTerms((prev) =>
        prev.filter((t) => {
          if (bulkDeleteSource && (t.source || '') !== bulkDeleteSource) return true;
          if (bulkDeleteCategory && (t.category || '') !== bulkDeleteCategory) return true;
          return false;
        }),
      );
      setBulkDeleteSource('');
      setBulkDeleteCategory('');
    } catch (err: any) {
      setError(err.message || 'Bulk delete failed');
    } finally {
      setBulkAction(null);
    }
  };

  const bulkDeletePreviewCount = useMemo(() => {
    if (!bulkDeleteSource && !bulkDeleteCategory) return 0;
    return terms.filter((t) => {
      if (bulkDeleteSource && (t.source || '') !== bulkDeleteSource) return false;
      if (bulkDeleteCategory && (t.category || '') !== bulkDeleteCategory) return false;
      return true;
    }).length;
  }, [terms, bulkDeleteSource, bulkDeleteCategory]);

  const SortHeader = ({ field, label, className }: { field: SortField; label: string; className?: string }) => (
    <th
      className={`px-3 py-2 font-medium cursor-pointer select-none hover:bg-gray-100 transition-colors ${className || ''}`}
      onClick={() => toggleSort(field)}
    >
      <div className="flex items-center gap-1">
        <span>{label}</span>
        {sortField === field ? (
          sortDir === 'asc' ? <ChevronUp size={12} className="text-blue-600" /> : <ChevronDown size={12} className="text-blue-600" />
        ) : (
          <ArrowUpDown size={11} className="text-gray-300" />
        )}
      </div>
    </th>
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Domain Terms</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {filtered.length === terms.length
              ? `${terms.length} terms — used for query expansion and chunk enrichment`
              : `${filtered.length} of ${terms.length} terms (filtered)`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search terms…"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(0); }}
              className="pl-7 pr-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 w-48"
            />
          </div>
          <button
            onClick={() => setShowColumnFilters((v) => !v)}
            title="Toggle column filters"
            className={`flex items-center gap-1 px-2.5 py-1.5 border rounded-md text-xs transition-colors ${
              showColumnFilters || hasActiveColumnFilters
                ? 'border-blue-400 text-blue-700 bg-blue-50 hover:bg-blue-100'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Filter size={13} />
            Column Filters
            {hasActiveColumnFilters && (
              <span className="ml-1 px-1.5 py-0.5 bg-blue-600 text-white rounded-full text-[9px] font-bold leading-none">
                {Object.entries(columnFilters).filter(([k, v]) => {
                  if (k === 'is_abbreviation' || k === 'verified') return v !== 'all';
                  return v !== '';
                }).length}
              </span>
            )}
          </button>
          {hasActiveColumnFilters && (
            <button
              onClick={clearAllFilters}
              title="Clear all filters"
              className="flex items-center gap-1 px-2 py-1.5 border border-red-200 text-red-600 rounded-md text-xs hover:bg-red-50 transition-colors"
            >
              <XCircle size={13} /> Clear
            </button>
          )}
          <button
            onClick={handleExport}
            disabled={!!bulkAction}
            title="Export all terms + synonyms as JSON"
            className="flex items-center gap-1 px-2.5 py-1.5 border border-gray-300 text-gray-700 rounded-md text-xs hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download size={13} /> Export
          </button>
          <button
            onClick={handleImport}
            disabled={!!bulkAction}
            title="Import terms + synonyms from JSON file"
            className="flex items-center gap-1 px-2.5 py-1.5 border border-gray-300 text-gray-700 rounded-md text-xs hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Upload size={13} /> Import
          </button>
          <button
            onClick={() => { setShowBulkDeleteDialog(true); setBulkDeleteSource(''); setBulkDeleteCategory(''); }}
            disabled={!!bulkAction}
            title="Delete terms by Source or Category filter"
            className="flex items-center gap-1 px-2.5 py-1.5 border border-orange-300 text-orange-600 rounded-md text-xs hover:bg-orange-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Filter size={13} /> Delete by Filter
          </button>
          <button
            onClick={handleWipe}
            disabled={!!bulkAction}
            title="Delete ALL domain terms and synonyms"
            className="flex items-center gap-1 px-2.5 py-1.5 border border-red-300 text-red-600 rounded-md text-xs hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <AlertOctagon size={13} /> Wipe All
          </button>
          <button
            onClick={() => openEditor(null)}
            disabled={!!bulkAction}
            className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-md text-xs font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus size={13} /> Add Term
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-start gap-2">
          <AlertTriangle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-red-700">{error}</p>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 flex items-start gap-2">
          <CheckCircle size={16} className="text-green-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-green-700">{success}</p>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <Loader2 size={20} className="animate-spin mx-auto text-gray-400" />
            <p className="text-xs text-gray-500 mt-2">Loading domain terms…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-center text-gray-500 text-xs">
            {searchQuery || hasActiveColumnFilters ? (
              <div className="space-y-2">
                <p>No terms match the current filters.</p>
                <button
                  onClick={clearAllFilters}
                  className="text-blue-600 hover:underline"
                >
                  Clear all filters
                </button>
              </div>
            ) : (
              'No domain terms found.'
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-gray-500 bg-gray-50">
                    <SortHeader field="term" label="Term" />
                    <SortHeader field="canonical_form" label="Canonical Form" />
                    <SortHeader field="category" label="Category" />
                    <SortHeader field="is_abbreviation" label="Abbr" />
                    <SortHeader field="full_form" label="Full Form" />
                    <SortHeader field="source" label="Source" />
                    <SortHeader field="confidence" label="Conf" />
                    <SortHeader field="verified" label="Verified" />
                    <SortHeader field="usage_count" label="Uses" />
                  </tr>

                  {/* Column filter row */}
                  {showColumnFilters && (
                    <tr className="border-b border-gray-200 bg-blue-50/40">
                      <td className="px-2 py-1.5">
                        <input
                          type="text"
                          placeholder="Filter…"
                          value={columnFilters.term}
                          onChange={(e) => updateColumnFilter('term', e.target.value)}
                          className="w-full text-[11px] border border-gray-200 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <input
                          type="text"
                          placeholder="Filter…"
                          value={columnFilters.canonical_form}
                          onChange={(e) => updateColumnFilter('canonical_form', e.target.value)}
                          className="w-full text-[11px] border border-gray-200 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <select
                          value={columnFilters.category}
                          onChange={(e) => updateColumnFilter('category', e.target.value)}
                          className="w-full text-[11px] border border-gray-200 rounded px-1 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
                        >
                          <option value="">All</option>
                          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </td>
                      <td className="px-2 py-1.5">
                        <select
                          value={columnFilters.is_abbreviation}
                          onChange={(e) => updateColumnFilter('is_abbreviation', e.target.value)}
                          className="w-full text-[11px] border border-gray-200 rounded px-1 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
                        >
                          <option value="all">All</option>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </select>
                      </td>
                      <td className="px-2 py-1.5">
                        <input
                          type="text"
                          placeholder="Filter…"
                          value={columnFilters.full_form}
                          onChange={(e) => updateColumnFilter('full_form', e.target.value)}
                          className="w-full text-[11px] border border-gray-200 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <select
                          value={columnFilters.source}
                          onChange={(e) => updateColumnFilter('source', e.target.value)}
                          className="w-full text-[11px] border border-gray-200 rounded px-1 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
                        >
                          <option value="">All</option>
                          {sources.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="px-2 py-1.5">
                        <input
                          type="number"
                          placeholder="≥"
                          min="0"
                          max="1"
                          step="0.1"
                          value={columnFilters.confidence}
                          onChange={(e) => updateColumnFilter('confidence', e.target.value)}
                          className="w-full text-[11px] border border-gray-200 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <select
                          value={columnFilters.verified}
                          onChange={(e) => updateColumnFilter('verified', e.target.value)}
                          className="w-full text-[11px] border border-gray-200 rounded px-1 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
                        >
                          <option value="all">All</option>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </select>
                      </td>
                      <td className="px-2 py-1.5">
                        <input
                          type="number"
                          placeholder="≥"
                          min="0"
                          value={columnFilters.usage_count}
                          onChange={(e) => updateColumnFilter('usage_count', e.target.value)}
                          className="w-full text-[11px] border border-gray-200 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
                        />
                      </td>
                    </tr>
                  )}
                </thead>
                <tbody>
                  {paginated.map((t) => (
                    <tr
                      key={t.id}
                      onClick={() => openEditor(t)}
                      className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="px-3 py-2 font-medium text-gray-900">{t.term}</td>
                      <td className="px-3 py-2 text-gray-600 max-w-xs truncate">{t.canonical_form}</td>
                      <td className="px-3 py-2">
                        {t.category && (
                          <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-[10px]">{t.category}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {t.is_abbreviation ? <span className="text-amber-600 font-medium">Yes</span> : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-3 py-2 text-gray-600 max-w-xs truncate">{t.full_form || '—'}</td>
                      <td className="px-3 py-2 text-gray-500">{t.source || '—'}</td>
                      <td className="px-3 py-2 text-gray-500">{t.confidence != null ? t.confidence.toFixed(2) : '—'}</td>
                      <td className="px-3 py-2 text-center">
                        {t.verified ? <CheckCircle size={13} className="text-green-500 inline" /> : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-3 py-2 text-gray-500">{t.usage_count ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-4 py-2 border-t border-gray-200 flex items-center justify-between bg-gray-50">
              <span className="text-xs text-gray-500">
                Showing {currentPage * PAGE_SIZE + 1}–{Math.min((currentPage + 1) * PAGE_SIZE, filtered.length)} of {filtered.length}
                {filtered.length !== terms.length && (
                  <span className="text-gray-400"> (filtered from {terms.length})</span>
                )}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(0)}
                  disabled={currentPage === 0}
                  className="px-1.5 py-1 rounded text-xs hover:bg-gray-200 disabled:opacity-30"
                  title="First page"
                >
                  ««
                </button>
                <button
                  onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                  disabled={currentPage === 0}
                  className="p-1 rounded hover:bg-gray-200 disabled:opacity-30"
                >
                  <ChevronLeft size={14} />
                </button>
                <span className="text-xs text-gray-600 px-2">
                  {currentPage + 1} / {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={currentPage >= totalPages - 1}
                  className="p-1 rounded hover:bg-gray-200 disabled:opacity-30"
                >
                  <ChevronRight size={14} />
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages - 1)}
                  disabled={currentPage >= totalPages - 1}
                  className="px-1.5 py-1 rounded text-xs hover:bg-gray-200 disabled:opacity-30"
                  title="Last page"
                >
                  »»
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Edit / Create Modal */}
      {editingTerm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">
                  {isNew ? 'Add Domain Term' : 'Edit Domain Term'}
                </h3>
                {!isNew && (
                  <p className="text-xs text-gray-500 mt-0.5">ID: {editingTerm.id}</p>
                )}
              </div>
              <button onClick={() => setEditingTerm(null)} className="p-1 hover:bg-gray-100 rounded">
                <X size={16} className="text-gray-500" />
              </button>
            </div>

            {/* Form */}
            <div className="px-5 py-4 space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Term <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formTerm}
                  onChange={(e) => setFormTerm(e.target.value)}
                  className="w-full text-xs border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. transship, BL, VGM"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Canonical Form</label>
                <input
                  type="text"
                  value={formCanonical}
                  onChange={(e) => setFormCanonical(e.target.value)}
                  className="w-full text-xs border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Canonical / display form"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Category</label>
                  <input
                    type="text"
                    value={formCategory}
                    onChange={(e) => setFormCategory(e.target.value)}
                    className="w-full text-xs border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g. equipment, operation"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Source</label>
                  <input
                    type="text"
                    value={formSource}
                    onChange={(e) => setFormSource(e.target.value)}
                    className="w-full text-xs border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g. manual, lrp_dictionary"
                  />
                </div>
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-1.5 text-xs">
                  <input
                    type="checkbox"
                    checked={formIsAbbr}
                    onChange={(e) => setFormIsAbbr(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  Is Abbreviation
                </label>
                <label className="flex items-center gap-1.5 text-xs">
                  <input
                    type="checkbox"
                    checked={formVerified}
                    onChange={(e) => setFormVerified(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  Verified
                </label>
              </div>

              {formIsAbbr && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Full Form</label>
                  <input
                    type="text"
                    value={formFullForm}
                    onChange={(e) => setFormFullForm(e.target.value)}
                    className="w-full text-xs border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g. Bill of Lading"
                  />
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Confidence ({formConfidence})
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={formConfidence}
                  onChange={(e) => setFormConfidence(e.target.value)}
                  className="w-full"
                />
              </div>

              {!isNew && editingTerm.updated_at && (
                <div className="bg-gray-50 rounded-lg px-3 py-2 text-[10px] text-gray-500 space-y-0.5">
                  <p>Created: {editingTerm.created_at ? new Date(editingTerm.created_at).toLocaleString() : '—'}</p>
                  <p>Updated: {new Date(editingTerm.updated_at).toLocaleString()}</p>
                  <p>Usage count: {editingTerm.usage_count ?? 0}</p>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <div>
                {!isNew && (
                  <button
                    onClick={handleDelete}
                    disabled={saving}
                    className="flex items-center gap-1 px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-md text-xs transition-colors disabled:opacity-50"
                  >
                    <Trash2 size={13} /> Delete
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setEditingTerm(null)}
                  className="px-3 py-1.5 text-gray-600 hover:bg-gray-100 rounded-md text-xs transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || !formTerm.trim()}
                  className="flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 text-white rounded-md text-xs font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete by Filter Dialog */}
      {showBulkDeleteDialog && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Delete Terms by Filter</h3>
                <p className="text-xs text-gray-500 mt-0.5">Select at least one filter to delete matching terms</p>
              </div>
              <button onClick={() => setShowBulkDeleteDialog(false)} className="p-1 hover:bg-gray-100 rounded">
                <X size={16} className="text-gray-500" />
              </button>
            </div>

            <div className="px-5 py-4 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Source</label>
                <select
                  value={bulkDeleteSource}
                  onChange={(e) => setBulkDeleteSource(e.target.value)}
                  className="w-full text-xs border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="">— Any source —</option>
                  {sources.map((s) => {
                    const count = terms.filter((t) => (t.source || '') === s).length;
                    return <option key={s} value={s}>{s} ({count} terms)</option>;
                  })}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={bulkDeleteCategory}
                  onChange={(e) => setBulkDeleteCategory(e.target.value)}
                  className="w-full text-xs border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="">— Any category —</option>
                  {categories.map((c) => {
                    const count = terms.filter((t) => (t.category || '') === c).length;
                    return <option key={c} value={c}>{c} ({count} terms)</option>;
                  })}
                </select>
              </div>

              {(bulkDeleteSource || bulkDeleteCategory) && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
                  <p className="text-xs text-orange-800 font-medium">
                    {bulkDeletePreviewCount} term{bulkDeletePreviewCount !== 1 ? 's' : ''} will be deleted
                  </p>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <button
                onClick={() => setShowBulkDeleteDialog(false)}
                className="px-3 py-1.5 text-gray-600 hover:bg-gray-100 rounded-md text-xs transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={!bulkDeleteSource && !bulkDeleteCategory}
                className="flex items-center gap-1.5 px-4 py-1.5 bg-orange-600 text-white rounded-md text-xs font-medium hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Trash2 size={13} /> Delete {bulkDeletePreviewCount > 0 ? `${bulkDeletePreviewCount} terms` : 'Matching'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk action loading overlay */}
      {bulkAction && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-2xl px-8 py-6 flex flex-col items-center gap-3 max-w-sm">
            <Loader2 size={28} className="animate-spin text-blue-600" />
            <p className="text-sm font-medium text-gray-900">{bulkAction}</p>
            <p className="text-xs text-gray-500">Please wait, do not close this page.</p>
          </div>
        </div>
      )}
    </div>
  );
}
